import React from "react";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";
import formOptions from "../../config/formOptions.json";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { graphqlEndpoint } from "../../config";
import { CREATE_AUDIT_QUESTION } from "../../graph/auditQuestions/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const auditQuestionSchema = yup.object().shape({
  industry: yup.string().trim().required("Industry is required"),
  area: yup.string().trim().required("Area is required"),
  statement: yup.string().nullable(),
  description: yup.string().nullable(),
  sortOrder: yup.number().integer("Sort order must be a whole number").nullable(),
  isActive: yup.boolean(),
});

const initialAuditQuestionValues = {
  industry: "",
  area: "",
  statement: "",
  description: "",
  sortOrder: "",
  isActive: true,
};


const CreateAuditQuestions = () => {
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbar();

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      const cleanedValues = { ...values };

      Object.keys(cleanedValues).forEach((key) => {
        if (cleanedValues[key] === "") cleanedValues[key] = null;
      });

      if (cleanedValues.sortOrder !== null) {
        cleanedValues.sortOrder = Number(cleanedValues.sortOrder);
      }

      await client.request(CREATE_AUDIT_QUESTION, {
        input: cleanedValues,
      });

      setSnackbar({
        open: true,
        message: "Audit question created successfully",
        severity: "success",
      });

      resetForm();
      navigate("/manageAuditQuestions");
    } catch (err) {
      const message =
        err?.response?.errors?.[0]?.message || "Unexpected error";

      console.error("Error creating audit question:", err);

      setSnackbar({
        open: true,
        message: `Failed to create audit question: ${message}`,
        severity: "error",
      });
    }
  };

  return (
    <Box>
      <Header
        title="CREATE AUDIT QUESTION"
        subtitle="Add a reusable operational audit question to the global question bank"
      />

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialAuditQuestionValues}
        validationSchema={auditQuestionSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <FormControl
                fullWidth
                sx={{ gridColumn: "span 2" }}
                error={!!touched.industry && !!errors.industry}
              >
                <InputLabel>Industry</InputLabel>
                <Select
                  name="industry"
                  value={values.industry}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Industry"
                >
                  {formOptions.industryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                sx={{ gridColumn: "span 2" }}
                error={!!touched.area && !!errors.area}
              >
                <InputLabel>Area</InputLabel>
                <Select
                  name="area"
                  value={values.area}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Area"
                >
                  {formOptions.operationalAreaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Sort Order"
                name="sortOrder"
                type="number"
                value={values.sortOrder}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.sortOrder && !!errors.sortOrder}
                helperText={touched.sortOrder && errors.sortOrder}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Statement"
                name="statement"
                value={values.statement}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.statement && !!errors.statement}
                helperText={touched.statement && errors.statement}
                multiline
                minRows={3}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                label="Description"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                multiline
                minRows={4}
                sx={{ gridColumn: "span 4" }}
              />

              <Box sx={{ gridColumn: "span 4" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.isActive}
                      onChange={(event) =>
                        setFieldValue("isActive", event.target.checked)
                      }
                      color="secondary"
                    />
                  }
                  label="Active"
                />
              </Box>
            </Box>

            <Box display="flex" justifyContent="end" mt="20px" gap={2}>
              <ActionButton
                tone="ghost"
                onClick={() => navigate("/createAuditQuestions")}
              >
                Cancel
              </ActionButton>

              <ActionButton type="submit" tone="success">
                Create Question
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateAuditQuestions;